import React, { useMemo } from 'react';
import { Drawer } from '@mui/material';
import { useSelector } from 'react-redux';
import CardDetailView from 'js/components/elements/cards/CardDetailView';
import { RESOURCE_TYPE_MARKETPLACE_DOMAINS } from 'js/constants/resourceTypes';
import { ResourceActivity } from 'new/elements/support/detailTabs/ResourceActivity';
import { DataSourceDetailHeader } from 'js/components/data_sources/cards/DataSourceDetailHeader';
import { DataSinkDetailHeader } from 'js/components/data_sinks/cards/DataSinkDetailHeader';
import { DataSetDetailHeader } from 'js/components/data_sets/cards/DataSetDetailHeader';
import { AvailableDataSetDetailHeader } from 'js/components/data_sets_available/cards/AvailableDataSetDetailHeader';
import { Stack } from 'new/elements/Stack';
import { DomainDetailHeader } from 'new/components/marketplace/components/DataDomainHeader/DomainDetailHeader';
import { DataDomainOverviewTab } from 'new/components/marketplace/components/DataDomainsOverviewTab';
import {
  getAllowedNexsetTabs,
  NEXSET_TAB_IDS,
  TAB_METADATA,
  type NexsetTabId,
} from 'new/utils/nexsetTabs';
import usePaging from 'new/state/paging/repository/usePaging';
import useEntities from 'new/state/entities/repository/useEntities';
import { dataSinkTabs, getNexsetTabComponent, getSourceTabs } from 'new/utils/resourceTabs';

interface DetailViewProps {
  data?: any;
  meta?: any;
  showTransform?: boolean;
  onClose?: () => void;
  /** Override the Drawer z-index (useful when rendered alongside MUI Dialogs) */
  zIndex?: number;
  /** Hide all header-level actions (Transform, Send, three-dot menu) */
  hideActions?: boolean;
}

const detailContentHeader = {
  SOURCE: {
    v2: DataSourceDetailHeader,
  },
  DATA_SINK: {
    v2: DataSinkDetailHeader,
  },
  DATA_SET: {
    v2: DataSetDetailHeader,
    shared_v2: AvailableDataSetDetailHeader,
  },
  DISCOVER: {
    v2: DataSetDetailHeader,
    shared_v2: AvailableDataSetDetailHeader,
  },
  DOMAIN: {
    v2: DomainDetailHeader,
  },
};

function getSelectedCardTabs({
  data: selectedCardData,
  getMetaInfoById,
  sources,
  parentDataSet,
  vendors = {},
}) {
  switch (selectedCardData.type) {
    case 'SOURCE':
      return getSourceTabs(selectedCardData, getMetaInfoById, vendors);

    case 'DATA_SINK':
      return dataSinkTabs(selectedCardData);

    case 'DOMAIN':
      return [
        {
          tabName: 'Overview',
          tabElement: <DataDomainOverviewTab data={selectedCardData} />,
        },
        {
          tabName: 'Activity',
          tabElement: <ResourceActivity resourceType={RESOURCE_TYPE_MARKETPLACE_DOMAINS} />,
        },
      ];

    case 'DISCOVER': {
      const flowType = selectedCardData?.flow_type;
      const sourceData = sources?.[selectedCardData?.data_source_id];

      const allowedTabIds = getAllowedNexsetTabs({
        data: selectedCardData,
        flowType,
        getMetaInfoById,
        dataSource: sourceData,
      });

      // DISCOVER page only shows Overview, Schema, and Stats
      const discoverAllowedTabs = [
        NEXSET_TAB_IDS.OVERVIEW,
        NEXSET_TAB_IDS.SCHEMA,
        NEXSET_TAB_IDS.STATS,
      ] as const;

      const filteredTabIds = allowedTabIds.filter((tabId) =>
        (discoverAllowedTabs as readonly string[]).includes(tabId),
      ) as NexsetTabId[];

      return filteredTabIds.map((tabId) => {
        const metadata = TAB_METADATA[tabId];
        return {
          tabName: metadata.label,
          tabElement: getNexsetTabComponent(tabId, selectedCardData),
        };
      });
    }

    default: {
      const flowType = selectedCardData?.flow_type;
      const sourceData = sources?.[selectedCardData?.data_source_id];

      const allowedTabIds = getAllowedNexsetTabs({
        data: selectedCardData,
        flowType,
        getMetaInfoById,
        dataSource: sourceData,
        parentDataSet,
      });

      return allowedTabIds.map((tabId) => {
        const metadata = TAB_METADATA[tabId];
        return {
          tabName: metadata.label,
          tabElement: getNexsetTabComponent(tabId, selectedCardData),
        };
      });
    }
  }
}

function HeaderContent({ data: selectedCardData, showTransform: showT, onClose }) {
  const publicDatasets = useSelector((state: any) => state.publicDatasets);
  const sharedDatasets = useSelector((state: any) => state.sharedDatasets);

  const shared = useMemo(
    () =>
      publicDatasets.byId.includes(selectedCardData.id) ||
      sharedDatasets.byId.includes(selectedCardData.id),
    [selectedCardData.id, publicDatasets, sharedDatasets],
  );
  let key = 'v2';
  let showTransform = showT !== undefined ? showT : true;
  if (selectedCardData.type === 'DATA_SET') {
    if (shared) {
      key = 'shared_v2';
    }
  }

  if (!selectedCardData.type) return null;

  const HeaderContent = detailContentHeader[selectedCardData.type][key];
  return (
    <HeaderContent
      data={selectedCardData}
      onCloseDrawer={onClose}
      showPipelineButton={false}
      showTransform={showTransform}
      showSend={!shared && showTransform}
      showMenu={!shared}
    />
  );
}

export const DetailView = (props: DetailViewProps) => {
  const { data, meta, onClose, showTransform, zIndex, hideActions } = props;
  const mode = useSelector((state: any) => state.app.listing.aclQueryMode);
  const { getMetaInfoById } = usePaging();
  const { data: sources } = useEntities('sources');
  const parentDataSet = useSelector(
    (state: any) => state.entities.dataSets[data?.parent_data_set_id],
  );
  const vendors = useSelector((state: any) => state.entities?.vendors ?? {});

  const options = {
    onClose,
    data,
    showTransform,
    getMetaInfoById,
    sources,
    parentDataSet,
    vendors,
  };

  return (
    <Drawer
      open={!!data}
      anchor="right"
      onClose={onClose}
      {...(zIndex != null ? { sx: { zIndex } } : {})}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: '1130px',
        },
      }}
    >
      <Stack height="100vh">
        {!!data ? (
          <CardDetailView
            noHeaderPadding
            data={data}
            tabs={getSelectedCardTabs(options)}
            detailHeaderContent={<HeaderContent {...options} />}
            getSourceMeta={() => meta}
            aclQueryMode={mode}
            hideActions={hideActions}
          />
        ) : null}
      </Stack>
    </Drawer>
  );
};
